import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Search, Sparkles, Theater, ArrowRight, MapPin, Calendar, User2, ArrowLeft } from "lucide-react";
import { fmtDate } from "@/lib/format";

const publicApi = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });

const fmt = fmtDate;

export default function Discover() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);

  useEffect(() => {
    publicApi.get("/public/auditions-directory")
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((p) =>
      (p.title || "").toLowerCase().includes(term) ||
      (p.theater_name || "").toLowerCase().includes(term) ||
      (p.director || "").toLowerCase().includes(term) ||
      (p.venue || "").toLowerCase().includes(term)
    );
  }, [items, q]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-white/5 sticky top-0 z-30 bg-[#0A0A0A]/85 backdrop-blur-lg" data-testid="discover-header">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md border border-gold/40 grid place-items-center bg-[#141210]"><Sparkles className="w-4 h-4 text-gold" /></div>
            <div className="font-display text-xl tracking-tight">StageCall</div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/" data-testid="discover-back-btn" className="text-sm text-zinc-400 hover:text-white px-3 py-2 inline-flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Home</Link>
            <Link to="/login" data-testid="discover-signin-btn" className="text-sm text-zinc-200 hover:text-white px-3 py-2">For companies</Link>
          </div>
        </div>
      </header>

      <section className="relative border-b border-white/5">
        <div className="absolute inset-0 opacity-30 grain-overlay" />
        <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 border border-gold/30 rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.24em] text-gold mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-gold" /> Public directory
            </div>
            <h1 className="font-display text-4xl md:text-6xl leading-tight tracking-tight font-light">
              Now casting.
              <br /><span className="text-gold">Find your next audition.</span>
            </h1>
            <p className="mt-5 text-zinc-400">Browse open calls from theatres running on StageCall. Every audition here uses the same signup form, custom questions, and slot picker — no more chasing PDFs.</p>
            <div className="mt-8 relative max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <Input
                data-testid="directory-search"
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search by theatre, show, or director…"
                className="pl-11 bg-[#0F0F0F] border-white/10 h-14 text-base"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-12">
        {items === null ? (
          <div className="text-zinc-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-lg border border-dashed border-white/10 p-16 text-center">
            <Theater className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
            <div className="font-display text-2xl text-zinc-300">No matches</div>
            <p className="text-zinc-500 text-sm mt-2">{q ? "Try a different search term." : "No published auditions right now — check back soon."}</p>
          </div>
        ) : (
          <>
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-4">Showing {filtered.length} {filtered.length === 1 ? "audition" : "auditions"}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((p) => (
                <article key={p.id} data-testid={`directory-card-${p.id}`}
                         className="rounded-xl border border-white/10 bg-[#0F0F0F] p-5 flex flex-col hover:border-gold/40 transition-colors">
                  <div className="flex items-start gap-3">
                    {p.theater_logo_url ? (
                      <img src={p.theater_logo_url} alt="" className="w-12 h-12 rounded object-contain bg-[#0A0A0A] border border-white/10 p-0.5 shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded bg-[#0A0A0A] border border-white/10 grid place-items-center text-zinc-600 shrink-0"><Theater className="w-5 h-5" /></div>
                    )}
                    <div className="min-w-0">
                      <div className="font-display text-lg leading-tight">{p.title}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5 truncate">{p.theater_name || "—"}</div>
                    </div>
                  </div>

                  {p.audition_pitch && (
                    <p className="mt-4 text-sm text-zinc-400 leading-relaxed line-clamp-4">{p.audition_pitch}</p>
                  )}

                  <div className="mt-4 space-y-1.5 text-xs text-zinc-400">
                    {p.director && <div className="inline-flex items-center gap-1.5"><User2 className="w-3.5 h-3.5 text-zinc-500" /> Dir. {p.director}</div>}
                    {p.venue && <div className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-zinc-500" /> {p.venue}</div>}
                    {p.opening_night && <div className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-zinc-500" /> Opens {fmt(p.opening_night)}</div>}
                  </div>

                  <div className="mt-5 pt-4 border-t border-white/5">
                    <a data-testid={`directory-signup-${p.id}`} href={`/audition/${p.enroll_code}`}
                       className="w-full h-10 rounded font-semibold text-sm inline-flex items-center justify-center gap-2 hover:brightness-110"
                       style={{ backgroundColor: "#F59E0B", color: "#0A0A0A" }}>
                      Audition now <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
      </section>

      <footer className="border-t border-white/5 py-8 mt-8">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between text-xs text-zinc-500">
          <div className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-gold" /> Powered by StageCall</div>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
